import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  downloadBlob,
  exportDownloadErrorMessage,
  filenameFromContentDisposition,
} from "../../Utils/Helper/downloadFile";
import { toast } from "../../Utils/Helper/toast";

type Executive = {
  user_id: string;
  email: string;
  full_name: string;
  position: string;
} | null;

type Account = {
  customer_id: string;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
} | null;

export type AssignmentApi = {
  id: string | null;
  accountId: string | null;
  customer_id: string | null;
  executiveId: string | null;
  primary_executive_id: string | null;
  status: string;
  assigned_date: string | null;
  due_date: string | null;
  completion_date: string | null;
  account: Account;
  primary_executive: Executive;
  secondaryExecutive: Executive;
  submitted_by: string | null;
  submitted_by_name: string | null;
  assigned_by: string | null;
  assigned_by_name: string | null;
  batch_name: string | null;
  batch_id: string | null;
  prep_activated_at: string | null;
  relationship_type: string | null;
  assignment_priority: string | null;
  assessment_id: string | null;
  assessment_title: string | null;
  assessment_type: string | null;
  type: string | null;
  created_at: string;
  updated_at: string;
  approval_status: string | null;
  notes: string | null;
};

type UnassignedCustomerApi = {
  location: string;
  customer_id: string;
  name: string;
  industry: string | null;
  tier: string;
  revenue: number;
  is_active: boolean;
  assignment_status?: string;
};

export type ExecutiveFromAPI = {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  region: string;
  grade: number;
  gradeLabel: string;
  accountLimit: number;
  current_accounts: number;
  utilization: number;
  matchScore: number;
  countryMatch: boolean;
  warnings: string[];
  recommendations: string[];
  availabilityStatus?: string | null;
  performanceScore?: number;
  specialties?: string[] | null;
  languages?: string[] | null;
};

export type APIRecommendationResponse = {
  bulk: {
    /** Array of primary sponsor executives (used for auto & manual UI) */
    primary_sponsor?: ExecutiveFromAPI[];
    customers: Array<{
      customer_id: string;
      name: string;
      /** AI recommended sponsor ID (auto mode) */
      primary_sponsor_id?: string;
      [key: string]: any;
    }>;
    secondary_sponsors: ExecutiveFromAPI[];
  };
  customer: any;
  results: any;
  primary: any;
  primary_sponsor?: ExecutiveFromAPI[];
  secondary: any;
  mode: "auto" | "manual";
  is_bulk: boolean;
  account_count: number;
};

export type PendingApprovalApi = {
  id: string;
  customer_id: string;
  primary_executive_id: string;
  secondary_executive_id: string | null;
  assignment_type: string;
  assigned_date: string | null;
  match_score: number | null;
  status: string;
  approval_status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  notes: string | null;
  submitted_by: string;
  rejected_by: string | null;
  rejected_at: string | null;
  submitted_by_name: string;
  submitted_at: string;
  approver_id: string;
  action: string | null;
  assignment_id: string | null;
  assignment_mode: string;
  batch_name: string;
  created_at: string;
  updated_at: string;
  account_name: string;
  primary_executive_name: string;
  executive_country: string | null;
  executive_region: string | null;
  executive_role: string | null;
};

type AssignmentState = {
  assignments: AssignmentApi[];
  prep_activated_assignments: AssignmentApi[];
  assessment_assignments: AssignmentApi[];
  total_assignments: number;
  total_prep_activated: number;
  total_assessments: number;
  pending_approvals: number;
  unassignedCustomers: UnassignedCustomerApi[];
  unassignedCustomersLoading: boolean;
  unassignedCustomersError: string | null;
  loading: boolean;
  error: string | null;
  recommendations: {
    data: APIRecommendationResponse | null;
    loading: boolean;
    error: string | null;
  };
  submitAssignment: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  pendingApprovals: {
    items: PendingApprovalApi[];
    loading: boolean;
    error: string | null;
  };
  approveRejectRequest: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  editAssignment: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  deleteAssignment: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
  stakeholdersExport: {
    loading: boolean;
    error: string | null;
  };
};

const initialState: AssignmentState = {
  assignments: [],
  prep_activated_assignments: [],
  assessment_assignments: [],
  total_assignments: 0,
  total_prep_activated: 0,
  total_assessments: 0,
  pending_approvals: 0,
  unassignedCustomers: [],
  unassignedCustomersLoading: false,
  unassignedCustomersError: null,
  loading: false,
  error: null,
  recommendations: {
    data: null,
    loading: false,
    error: null,
  },
  submitAssignment: {
    loading: false,
    error: null,
    success: false,
  },
  pendingApprovals: {
    items: [],
    loading: false,
    error: null,
  },
  approveRejectRequest: {
    loading: false,
    error: null,
    success: false,
  },
  editAssignment: {
    loading: false,
    error: null,
    success: false,
  },
  deleteAssignment: {
    loading: false,
    error: null,
    success: false,
  },
  stakeholdersExport: {
    loading: false,
    error: null,
  },
};

interface AssignmentResponse {
  assignments: AssignmentApi[];
  prep_activated_assignments: AssignmentApi[];
  assessment_assignments: AssignmentApi[];
  total_assignments: number;
  total_prep_activated: number;
  total_assessments: number;
  pending_approvals: number;
}

export const fetchAssignments = createAsyncThunk<
  AssignmentResponse,
  void,
  { rejectValue: string }
>("assignments/fetchAssignments", async (_, { rejectWithValue }) => {
  try {
    const url = API_URLS.assignments();
    const res = await get(url);
    return {
      assignments: res.assignments || [],
      prep_activated_assignments: res.prep_activated_assignments || [],
      assessment_assignments: res.assessment_assignments || [],
      total_assignments: res.total_assignments || 0,
      total_prep_activated: res.total_prep_activated || 0,
      total_assessments: res.total_assessments || 0,
      pending_approvals: res.pending_approvals || 0,
    };
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(parsed?.message || "Failed to fetch assignments");
    } catch (_) {
      return rejectWithValue("Failed to fetch assignments");
    }
  }
});

export const fetchUnassignedCustomers = createAsyncThunk<
  UnassignedCustomerApi[],
  void,
  { rejectValue: string }
>("assignments/fetchUnassignedCustomers", async (_, { rejectWithValue }) => {
  try {
    const url = API_URLS.getUnassignedCustomers();
    const res = await get(url);
    return Array.isArray(res) ? res : [];
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(
        parsed?.message || "Failed to fetch unassigned customers"
      );
    } catch (_) {
      return rejectWithValue("Failed to fetch unassigned customers");
    }
  }
});

interface FetchRecommendationsPayload {
  accountIds: string[];
  mode: "auto" | "manual";
}

export const fetchRecommendations = createAsyncThunk<
  APIRecommendationResponse,
  FetchRecommendationsPayload,
  { rejectValue: string }
>("assignments/fetchRecommendations", async (payload, { rejectWithValue }) => {
  if (payload.accountIds.length === 0) {
    return rejectWithValue("No accounts selected");
  }

  try {
    const url = API_URLS.getRecommendUnified();
    // Keep the loading screen visible for at least 2s (moved from backend sleep).
    const [response] = await Promise.all([
      post(url, {
        accountIds: payload.accountIds,
        mode: payload.mode,
      }),
      new Promise<void>(resolve => setTimeout(resolve, 2000)),
    ]);
    return response as APIRecommendationResponse;
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(
        parsed?.message || "Failed to fetch recommendations"
      );
    } catch (_) {
      return rejectWithValue("Failed to fetch recommendations");
    }
  }
});

interface SubmitAssignmentPayload {
  accountId: string;
  accountName: string;
  primaryExecutiveId: string;
  secondaryExecutiveIds?: string[];
  notes?: string;
  assignmentMode: "auto" | "manual";
  batchName: string;
  approverId: string;
}

export const submitAssignment = createAsyncThunk<
  any,
  SubmitAssignmentPayload,
  { rejectValue: string }
>("assignments/submitAssignment", async (payload, { rejectWithValue }) => {
  try {
    const url = API_URLS.assignments();
    const response = await post(url, payload);
    return response;
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(parsed?.message || "Failed to submit assignment");
    } catch (_) {
      return rejectWithValue("Failed to submit assignment");
    }
  }
});

export const fetchPendingApprovals = createAsyncThunk<
  PendingApprovalApi[],
  void,
  { rejectValue: string }
>("assignments/fetchPendingApprovals", async (_, { rejectWithValue }) => {
  try {
    const url = API_URLS.getPendingApprovals();
    const response = await get(url);
    return Array.isArray(response) ? response : [];
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(
        parsed?.message || "Failed to fetch pending approvals"
      );
    } catch (_) {
      return rejectWithValue("Failed to fetch pending approvals");
    }
  }
});

interface ApproveRejectRequestPayload {
  request_id: string;
  action: "approve" | "reject";
  notes?: string;
}

export const approveOrRejectRequestAssessment = createAsyncThunk<
  any,
  ApproveRejectRequestPayload,
  { rejectValue: string }
>(
  "assignments/approveOrRejectRequestAssessment",
  async (payload, { rejectWithValue }) => {
    try {
      const url = API_URLS.approveOrRejectRequestAssessment();
      const response = await post(url, payload);
      return response;
    } catch (err: any) {
      try {
        const parsed = JSON.parse(err?.message || "{}");
        return rejectWithValue(
          parsed?.message || "Failed to approve/reject request"
        );
      } catch (_) {
        return rejectWithValue("Failed to approve/reject request");
      }
    }
  }
);

interface EditAssignmentPayload {
  accountId: string;
  accountName: string;
  primaryExecutiveId: string;
  secondaryExecutiveIds?: string[];
  approverId: string;
  batchName: string;
  assignmentMode: "auto" | "manual";
  notes?: string;
}

export const editAssignment = createAsyncThunk<
  any,
  EditAssignmentPayload,
  { rejectValue: string }
>("assignments/editAssignment", async (payload, { rejectWithValue }) => {
  try {
    const url = API_URLS.editAssignmentRequest();
    const response = await post(url, payload);
    return response;
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(parsed?.message || "Failed to edit assignment");
    } catch (_) {
      return rejectWithValue("Failed to edit assignment");
    }
  }
});

interface DeleteAssignmentPayload {
  accountId: string;
  accountName: string;
  primaryExecutiveId: string;
  secondaryExecutiveIds?: string[];
  approverId: string;
  batchName: string;
  notes?: string;
}

export const deleteAssignment = createAsyncThunk<
  any,
  DeleteAssignmentPayload,
  { rejectValue: string }
>("assignments/deleteAssignment", async (payload, { rejectWithValue }) => {
  try {
    const url = API_URLS.deleteAssignmentRequest();
    const response = await post(url, payload);
    return response;
  } catch (err: any) {
    try {
      const parsed = JSON.parse(err?.message || "{}");
      return rejectWithValue(parsed?.message || "Failed to delete assignment");
    } catch (_) {
      return rejectWithValue("Failed to delete assignment");
    }
  }
});

async function stakeholdersExportErrorMessage(
  error: any,
  fallback: string
): Promise<string> {
  return exportDownloadErrorMessage(error, fallback);
}

export type StakeholderExportFormat = "csv" | "excel";

export const exportStakeholders = createAsyncThunk(
  "assignments/stakeholdersExport/download",
  async (format: StakeholderExportFormat, { rejectWithValue }) => {
    const isExcel = format === "excel";
    const url = API_URLS.exportStakeholders(format);
    const fallbackFilename = `stakeholder_export_${new Date().toISOString().slice(0, 10)}.${
      isExcel ? "xlsx" : "csv"
    }`;
    const fallbackError = isExcel
      ? "Failed to export stakeholders Excel"
      : "Failed to export stakeholders CSV";

    try {
      const response = await get(url, {
        responseType: "blob",
        returnFullResponse: true,
      });
      const blob = response.data as Blob;
      const filename = filenameFromContentDisposition(
        response.headers?.["content-disposition"] as string | undefined,
        fallbackFilename
      );
      downloadBlob(blob, filename);
      return filename;
    } catch (error: any) {
      const errorMessage = await stakeholdersExportErrorMessage(
        error,
        fallbackError
      );
      return rejectWithValue(errorMessage);
    }
  }
);

const AssignmentSlice = createSlice({
  name: "assignments",
  initialState,
  reducers: {
    clearAssignments(state) {
      state.assignments = [];
      state.prep_activated_assignments = [];
      state.assessment_assignments = [];
      state.total_assignments = 0;
      state.total_prep_activated = 0;
      state.total_assessments = 0;
      state.pending_approvals = 0;
      state.error = null;
      state.loading = false;
    },
    clearUnassignedCustomers(state) {
      state.unassignedCustomers = [];
      state.unassignedCustomersError = null;
      state.unassignedCustomersLoading = false;
    },
    clearRecommendations(state) {
      state.recommendations.data = null;
      state.recommendations.error = null;
      state.recommendations.loading = false;
    },
    clearSubmitAssignment(state) {
      state.submitAssignment.loading = false;
      state.submitAssignment.error = null;
      state.submitAssignment.success = false;
    },
    clearPendingApprovals(state) {
      state.pendingApprovals.items = [];
      state.pendingApprovals.error = null;
      state.pendingApprovals.loading = false;
    },
    clearApproveRejectRequest(state) {
      state.approveRejectRequest.loading = false;
      state.approveRejectRequest.error = null;
      state.approveRejectRequest.success = false;
    },
    clearEditAssignment(state) {
      state.editAssignment.loading = false;
      state.editAssignment.error = null;
      state.editAssignment.success = false;
    },
    clearDeleteAssignment(state) {
      state.deleteAssignment.loading = false;
      state.deleteAssignment.error = null;
      state.deleteAssignment.success = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAssignments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAssignments.fulfilled,
        (state, action: PayloadAction<AssignmentResponse>) => {
          state.loading = false;
          state.assignments = action.payload.assignments;
          state.prep_activated_assignments =
            action.payload.prep_activated_assignments;
          state.assessment_assignments = action.payload.assessment_assignments;
          state.total_assignments = action.payload.total_assignments;
          state.total_prep_activated = action.payload.total_prep_activated;
          state.total_assessments = action.payload.total_assessments;
          state.pending_approvals = action.payload.pending_approvals;
        }
      )
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch assignments";
      })
      .addCase(fetchUnassignedCustomers.pending, state => {
        state.unassignedCustomersLoading = true;
        state.unassignedCustomersError = null;
      })
      .addCase(
        fetchUnassignedCustomers.fulfilled,
        (state, action: PayloadAction<UnassignedCustomerApi[]>) => {
          state.unassignedCustomersLoading = false;
          state.unassignedCustomers = action.payload;
        }
      )
      .addCase(fetchUnassignedCustomers.rejected, (state, action) => {
        state.unassignedCustomersLoading = false;
        state.unassignedCustomersError =
          (action.payload as string) || "Failed to fetch unassigned customers";
      })
      .addCase(fetchRecommendations.pending, state => {
        state.recommendations.loading = true;
        state.recommendations.error = null;
      })
      .addCase(
        fetchRecommendations.fulfilled,
        (state, action: PayloadAction<APIRecommendationResponse>) => {
          state.recommendations.loading = false;
          state.recommendations.data = action.payload;
          state.recommendations.error = null;
        }
      )
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.recommendations.loading = false;
        state.recommendations.error =
          (action.payload as string) || "Failed to fetch recommendations";
        state.recommendations.data = null;
      })
      .addCase(submitAssignment.pending, state => {
        state.submitAssignment.loading = true;
        state.submitAssignment.error = null;
        state.submitAssignment.success = false;
      })
      .addCase(submitAssignment.fulfilled, state => {
        state.submitAssignment.loading = false;
        state.submitAssignment.success = true;
        state.submitAssignment.error = null;
      })
      .addCase(submitAssignment.rejected, (state, action) => {
        state.submitAssignment.loading = false;
        state.submitAssignment.error =
          (action.payload as string) || "Failed to submit assignment";
        state.submitAssignment.success = false;
      })
      .addCase(fetchPendingApprovals.pending, state => {
        state.pendingApprovals.loading = true;
        state.pendingApprovals.error = null;
      })
      .addCase(
        fetchPendingApprovals.fulfilled,
        (state, action: PayloadAction<PendingApprovalApi[]>) => {
          state.pendingApprovals.loading = false;
          state.pendingApprovals.items = action.payload;
          state.pendingApprovals.error = null;
        }
      )
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.pendingApprovals.loading = false;
        state.pendingApprovals.error =
          (action.payload as string) || "Failed to fetch pending approvals";
        state.pendingApprovals.items = [];
      })
      .addCase(approveOrRejectRequestAssessment.pending, state => {
        state.approveRejectRequest.loading = true;
        state.approveRejectRequest.error = null;
        state.approveRejectRequest.success = false;
      })
      .addCase(approveOrRejectRequestAssessment.fulfilled, state => {
        state.approveRejectRequest.loading = false;
        state.approveRejectRequest.success = true;
        state.approveRejectRequest.error = null;
      })
      .addCase(approveOrRejectRequestAssessment.rejected, (state, action) => {
        state.approveRejectRequest.loading = false;
        state.approveRejectRequest.error =
          (action.payload as string) || "Failed to approve/reject request";
        state.approveRejectRequest.success = false;
      })
      .addCase(editAssignment.pending, state => {
        state.editAssignment.loading = true;
        state.editAssignment.error = null;
        state.editAssignment.success = false;
      })
      .addCase(editAssignment.fulfilled, state => {
        state.editAssignment.loading = false;
        state.editAssignment.success = true;
        state.editAssignment.error = null;
      })
      .addCase(editAssignment.rejected, (state, action) => {
        state.editAssignment.loading = false;
        state.editAssignment.error =
          (action.payload as string) || "Failed to edit assignment";
        state.editAssignment.success = false;
      })
      .addCase(deleteAssignment.pending, state => {
        state.deleteAssignment.loading = true;
        state.deleteAssignment.error = null;
        state.deleteAssignment.success = false;
      })
      .addCase(deleteAssignment.fulfilled, state => {
        state.deleteAssignment.loading = false;
        state.deleteAssignment.success = true;
        state.deleteAssignment.error = null;
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.deleteAssignment.loading = false;
        state.deleteAssignment.error =
          (action.payload as string) || "Failed to delete assignment";
        state.deleteAssignment.success = false;
      })
      .addCase(exportStakeholders.pending, state => {
        state.stakeholdersExport.loading = true;
        state.stakeholdersExport.error = null;
      })
      .addCase(exportStakeholders.fulfilled, state => {
        state.stakeholdersExport.loading = false;
        state.stakeholdersExport.error = null;
      })
      .addCase(exportStakeholders.rejected, (state, action) => {
        state.stakeholdersExport.loading = false;
        state.stakeholdersExport.error =
          (action.payload as string) || "Failed to export stakeholders";
        toast.error(
          (action.payload as string) || "Failed to export stakeholders"
        );
      });
  },
});

export const {
  clearAssignments,
  clearUnassignedCustomers,
  clearRecommendations,
  clearSubmitAssignment,
  clearPendingApprovals,
  clearApproveRejectRequest,
  clearEditAssignment,
  clearDeleteAssignment,
} = AssignmentSlice.actions;
export default AssignmentSlice.reducer;
